import * as psl from 'psl';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@/components/chariot-ui';
import { Entitled } from '@/components/Entitled';
import {
  createAssetExportConfig,
  SourceOption,
} from '@/components/export/configs/asset.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { NoData } from '@/components/legacy-ui/NoData';
import { Loader } from '@/components/Loader';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { combineAssetNodeResults, useUpdateAsset } from '@/hooks/useAssets';
import { useAxios } from '@/hooks/useAxios';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { queryClient } from '@/queryclient';
import { AssetActions } from '@/sections/asset/components/actions';
import {
  AssetDrawerContent,
  AssetDrawerHeader,
  AssetDrawerModals,
  AssetDrawerTabs,
} from '@/sections/asset/components/drawer';
import { ConstantineRepoModal } from '@/sections/asset/components/modals/ConstantineRepoModal';
import {
  AssetRelationshipGraph,
  AttributesMapSection,
  DomainSection,
  OriginationSection,
  type OverviewTabAttributeList,
  VulnerabilitySummary,
} from '@/sections/asset/components/overview';
import { getAssetAffiliationOverview } from '@/sections/asset/components/tabs';
import {
  useAssetAttributeProcessing,
  useAssetDrawerData,
  useAssetDrawerTab,
  useAssetModalState,
  useAssetOverviewQueries,
  useAssetTabs,
} from '@/sections/asset/hooks/drawer';
import { processAssetOriginations } from '@/sections/asset/utils/overview';
import { parseKeys } from '@/sections/SearchByType';
import { useGlobalState } from '@/state/global.state';
import { Asset, RiskCombinedStatusCount, RiskSeverity } from '@/types';
import { cn } from '@/utils/classname';
import { buildSourceOptions } from '@/utils/sourceOptions.util';

/**
 * AssetDrawer
 *
 * Main drawer component for displaying comprehensive asset information including
 * overview, vulnerabilities, attributes, notes, and history tabs.
 *
 * Coordinates data fetching, state management, and renders modular sub-components
 * for header, tabs, content, and modals.
 *
 * Automatically optimized via React Compiler.
 */
export function AssetDrawer() {
  'use memo';

  const { canWriteAssets } = useEntitlements();
  const { drawerState } = useGlobalState();
  // Local state for tab management - prevents race conditions from React Router
  const [currentTab, setCurrentTab] = useAssetDrawerTab();

  const { key: assetKey } = parseKeys.assetKey(
    drawerState.assetDrawerKey || ''
  );

  // Modal state management
  const {
    isCommentModalOpen,
    setIsCommentModalOpen,
    isTagModalOpen,
    setIsTagModalOpen,
    isExportModalOpen,
    setIsExportModalOpen,
  } = useAssetModalState();

  const [isConstantineModalOpen, setIsConstantineModalOpen] = useState(false);

  // Coordinated data fetching for all drawer queries
  const {
    assetAttributes,
    riskCounts,
    technologiesTree,
    asset,
    entityGroup,
    entityIdentifier,
    isInitialLoading: drawerDataIsInitialLoading,
    statuses: { assets: assetsStatus },
  } = useAssetDrawerData(assetKey || '');

  const isRepository = asset?.key?.startsWith('#repository#') ?? false;
  const repoUrl = isRepository ? (entityGroup ?? '') : '';

  const axios = useAxios();

  // Fetch capabilities for consistent integration display names
  const { data: capabilitiesData } = useGetCapabilities();
  const { mutateAsync: updateAsset } = useUpdateAsset();

  // Fetch origin statistics for export functionality
  const { data: sourceStatistics, status: sourceStatisticsStatus } = useMy({
    resource: 'statistic',
    query: 'asset#origin',
  });

  // Build source options for export dropdown
  const sourceOptions: SourceOption[] = buildSourceOptions(
    sourceStatistics,
    capabilitiesData.allCapabilities
  );

  // Create dynamic export config with source options
  const assetExportConfig = createAssetExportConfig(sourceOptions);

  const isAssetGroupADomain =
    entityGroup && typeof entityGroup === 'string'
      ? psl.isValid(entityGroup)
      : false;

  // Cast riskCounts to Record<string, number> since useCounts actually returns
  // a status->count map (e.g., { OC: 5, OH: 10 }), not the full Statistics type
  const riskCountsMap = riskCounts as unknown as
    | Record<string, number>
    | undefined;

  const assetWithRisk = asset && {
    ...asset,
    riskSummary: {
      [RiskSeverity.Critical]: riskCountsMap?.['OC'] || 0,
      [RiskSeverity.High]: riskCountsMap?.['OH'] || 0,
      [RiskSeverity.Medium]: riskCountsMap?.['OM'] || 0,
      [RiskSeverity.Low]: riskCountsMap?.['OL'] || 0,
      [RiskSeverity.Info]: riskCountsMap?.['OI'] || 0,
      [RiskSeverity.Exposure]: riskCountsMap?.['OE'] || 0,
    },
  };

  // Process attributes for port/protocol matching and technologies
  const { attributes, technologies } = useAssetAttributeProcessing(
    assetAttributes,
    technologiesTree
  );

  const assetTabOptions = useAssetTabs(asset);

  const isInitialLoading =
    currentTab === 'crawler' ? false : drawerDataIsInitialLoading;

  if (isInitialLoading) {
    return (
      <div className="flex size-full items-center justify-center p-6">
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  const userDefinedTags = asset?.tags || [];

  const handleComment = async (comment: string) => {
    const assetIdentifier = parseKeys.assetKey(asset.key).identifier;

    await updateAsset({
      key: asset.key,
      name: assetIdentifier,
      comment,
      showSnackbar: false, // Suppress hook toast (show specific toast below)
    });
    toast.success('Note added to asset');
  };
  const handleRemoveTag = async (tagToRemove: string) => {
    try {
      const updatedTags = (asset?.tags || []).filter(
        tag => tag !== tagToRemove
      );
      await axios.put('/asset', {
        key: asset.key,
        tags: updatedTags,
      });

      // Use predicate-based invalidation to catch ALL asset queries
      // This matches the pattern from useUpdateSubscription
      // NOTE: UniqueQueryKeys.MY is 'MY' (uppercase), not 'my'
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          // Invalidate any 'MY' query for 'asset' resource
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });

      toast.success('Tag removed');
    } catch (error) {
      console.error('[AssetDrawer] Tag removal failed:', error);
      toast.error('Failed to remove tag');
    }
  };

  return (
    <div className="group flex size-full flex-col gap-6 p-6 first:gap-0">
      <AssetDrawerHeader
        asset={asset}
        entityIdentifier={entityIdentifier}
        entityGroup={entityGroup}
        assetsStatus={assetsStatus}
        tags={userDefinedTags}
        onRemoveTag={handleRemoveTag}
      />
      <Entitled entitlement="write_assets">
        <AssetActions
          assets={asset ? [asset] : []}
          onCommentClick={() => setIsCommentModalOpen(true)}
          onTagClick={() => setIsTagModalOpen(true)}
          onExportClick={() => setIsExportModalOpen(true)}
          onConstantineClick={
            isRepository && repoUrl
              ? () => setIsConstantineModalOpen(true)
              : undefined
          }
          hideLabel
        />
      </Entitled>
      <div className="bg-chariot-stroke h-px w-full shrink-0"></div>
      <AssetDrawerTabs
        assetTabOptions={assetTabOptions}
        entityGroup={entityGroup}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
      />
      <AssetDrawerContent
        asset={asset}
        assetKey={assetKey}
        assetWithRisk={assetWithRisk}
        riskCounts={riskCounts as RiskCombinedStatusCount}
        isInitialLoading={isInitialLoading}
        attributes={attributes}
        technologies={technologies}
        entityIdentifier={entityIdentifier}
        entityGroup={entityGroup}
        isAssetGroupADomain={isAssetGroupADomain}
        AssetOverview={AssetOverview}
        onOpenCommentModal={
          canWriteAssets ? () => setIsCommentModalOpen(true) : undefined
        }
        currentTab={currentTab}
        onTabChange={setCurrentTab}
      />
      <AssetDrawerModals
        asset={asset}
        isCommentModalOpen={isCommentModalOpen}
        setIsCommentModalOpen={setIsCommentModalOpen}
        isTagModalOpen={isTagModalOpen}
        setIsTagModalOpen={setIsTagModalOpen}
        handleComment={handleComment}
      />
      {isRepository && (
        <ConstantineRepoModal
          open={isConstantineModalOpen}
          onClose={() => setIsConstantineModalOpen(false)}
          repoUrl={repoUrl}
        />
      )}
      {sourceStatisticsStatus === 'success' && (
        <UniversalExportModal
          config={assetExportConfig}
          open={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          selectedRows={asset?.key ? [asset.key] : []}
          onClearSelection={() => setIsExportModalOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Domain information for asset overview
 */
interface DomainInfo {
  name: string;
  registrantOrg?: string;
  domainOrigination?: string;
  discoveredCapability?: string;
}

/**
 * Props for AssetOverview component
 */
interface AssetOverviewProps {
  attributesByName: Record<
    OverviewTabAttributeList,
    { value: string | number }[]
  >;
  domain?: DomainInfo;
  asset: Asset;
  riskCounts: RiskCombinedStatusCount;
  isAssetIdentifierAnIpAddress: boolean;
  isAssetIdentifierACloudResource: boolean;
  /** Tab change handler for local state management (prevents race conditions) */
  onTabChange?: (tab: string) => void;
}

/**
 * AssetOverview Component
 *
 * Displays comprehensive asset overview including vulnerabilities, attributes,
 * origination, domain information, and relationship graph.
 */
function AssetOverview({
  attributesByName,
  domain,
  asset,
  riskCounts,
  isAssetIdentifierAnIpAddress,
  isAssetIdentifierACloudResource,
  onTabChange,
}: AssetOverviewProps) {
  const [, setSearchParams] = useSearchParams();

  // Fetch all graph relationship data for the overview
  const { parentAssets, ultimateOrigins, childAssets } =
    useAssetOverviewQueries(asset.key);

  const [viewMore, setViewMore] = useState(false);
  const attributeExists = Boolean(
    Object.values(attributesByName).find(attributes => attributes.length > 0)
  );

  const originations = processAssetOriginations(
    asset,
    ultimateOrigins,
    parentAssets
  );

  // Process parent and child assets for the graph
  let processedParentAssets: Asset[] = [];
  if (parentAssets && Array.isArray(parentAssets)) {
    // Flatten all parent assets from tree nodes
    const allParentAssets = parentAssets
      .flatMap(node => combineAssetNodeResults(node))
      .filter(
        parentAsset =>
          parentAsset && parentAsset.key && parentAsset.key !== asset.key
      );

    // Deduplicate using Map with asset.key as unique identifier
    // Multiple TreeNodes can contain the same asset, causing duplicates
    processedParentAssets = Array.from(
      new Map(allParentAssets.map(asset => [asset.key, asset])).values()
    );
  }

  const parentAssetKeys =
    parentAssets && Array.isArray(parentAssets)
      ? parentAssets
          .flatMap(node => node.node.result.assets ?? [])
          .map(a => a.key)
          .filter(Boolean)
      : [];

  let processedChildAssets: Asset[] = [];
  if (childAssets && Array.isArray(childAssets)) {
    // Flatten all child assets from tree nodes
    const allChildAssets = childAssets
      .flatMap(node => combineAssetNodeResults(node))
      .filter(
        childAsset =>
          childAsset &&
          childAsset.key &&
          childAsset.key !== asset.key &&
          !parentAssetKeys.includes(childAsset.key)
      );

    // Deduplicate using Map with asset.key as unique identifier
    // Multiple TreeNodes can contain the same asset, causing duplicates
    processedChildAssets = Array.from(
      new Map(allChildAssets.map(asset => [asset.key, asset])).values()
    );
  }

  const assetAffiliationOverview = getAssetAffiliationOverview({ asset });

  const originationsExists = originations.length > 0;
  const riskCountsExist = Boolean(
    riskCounts && Object.keys(riskCounts).length > 0
  );
  const domainExists = Boolean(domain);
  const graphDataExists =
    processedParentAssets.length > 0 || processedChildAssets.length > 0;

  const dataExists =
    attributeExists ||
    originationsExists ||
    riskCountsExist ||
    graphDataExists ||
    domainExists;

  return (
    <div
      className={cn(
        'flex min-h-full flex-col gap-6 overflow-auto p-6',
        !dataExists && 'size-full items-center'
      )}
      data-testid="asset-overview"
    >
      {!dataExists && (
        <NoData
          icon={
            <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
          }
          title="No Information Collected"
          className="text-center text-sm"
        />
      )}
      {dataExists && (
        <>
          <VulnerabilitySummary riskCounts={riskCounts} />

          {(isAssetIdentifierAnIpAddress ||
            isAssetIdentifierACloudResource) && (
            <AttributesMapSection
              attributesByName={attributesByName}
              isAssetIdentifierAnIpAddress={isAssetIdentifierAnIpAddress}
              isAssetIdentifierACloudResource={isAssetIdentifierACloudResource}
            />
          )}

          <OriginationSection
            originations={originations}
            viewMore={viewMore}
            setViewMore={setViewMore}
            assetAffiliationOverview={assetAffiliationOverview}
            setSearchParams={setSearchParams}
            onTabChange={onTabChange}
          />

          {domain && <DomainSection domain={domain} />}

          <AssetRelationshipGraph
            processedParentAssets={processedParentAssets}
            processedChildAssets={processedChildAssets}
            asset={asset}
          />
        </>
      )}
    </div>
  );
}
